<script>
   /***********************************************/
   /* Svelte component with main app container    */
   /***********************************************/

   import DataLoader from './DataLoader.svelte';
   import PCVCalculator from './PCVCalculator.svelte';
   import ScalarsCalculator from './ScalarsCalculator.svelte';
   import ModelCalculator from './ModelCalculator.svelte';
   import AppPlots from './AppPlots.svelte';

   import { ScalarsPlot } from './plots';

   let dataset = null;      // calibration set
   let model = null;        // global PLS model
   let pvset = null;        // computed PV-set
   let pvoptions = null;    // PV-set options

   let scalars_ini = null;  // matrix with c_k/c scalars (D) computed for all components
   let scalars = null;      // matrix with c_k/c scalars (D) computed for selected components

   let calres = null;       // calibration results
   let pvres = null;        // PV-set results

   // combine both results to one object
   $: res = {cal: calres, pv: pvres};

   // if dataset is null - reset model
   $: if (dataset === null) {
      model = null;
   }

   // if model is null - reset pv-set and initial scalars
   $: if (model === null) {
      pvset = null;
      scalars_ini = null;
   }

   // if pv-set is null - reset all result objects
   $: if (pvset === null) {
      pvres = null;
      calres = null;
      scalars = null;
   }

   // if preliminary calculations set to null - reset current PV-set
   $: if (scalars_ini === null) {
      pvset = null;
   }
</script>

<div class="app-container">

   <!-- left column with main app controls -->
   <div class="app-controls">
      <div class="data-loader">
         <DataLoader bind:dataset={dataset} />
      </div>
      {#if dataset }
      <div class="model-calculator">
         <ModelCalculator {dataset} bind:model={model} />
      </div>
      {/if}
      {#if dataset && model }
      <div class="pcv-calculator">
         <ScalarsCalculator {dataset} {model}
            bind:scalars_ini={scalars_ini} bind:pvoptions={pvoptions} />
      </div>
      {/if}
      {#if dataset && model && scalars_ini }
      <div class="pcv-calculator">
         <PCVCalculator {dataset} {model} bind:pvoptions={pvoptions}
            bind:calres={calres} bind:pvset={pvset} bind:pvres={pvres} bind:scalars={scalars}/>
      </div>
      {/if}
   </div>

   <!-- right column with help text or with plots and plot controls -->
   <div class="app-content">
      {#if !pvset || !pvres}
         <div class="help-text">
         {#if dataset === null}
            <p><strong>Step 1.</strong></p>
            <p>
               Load your calibration set from a CSV file. The file should contain numeric values delimited
               by comma (with dot as decimal separator) or by semicolon (with comma as decimal separator). Rows correspond to observations or objects, columns — to variables. First row can contain variable names (header) and first column can contain labels for the objects/observations — just set the corresponding options after selecting
               the file.
            </p><p>
               For PLS version you must provide both predictors (X) and response (Y). The response values must be located in the first column in the CSV file. If first the column contains row labels, then the next column will be treated as response.
            </p>
         {:else}
            {#if model === null}
            <p><strong>Step 2</strong></p>
            <p>
               Next step is to create a global PLS model for the training set. Use more components/latent variables (LVs)
               than you expect to be optimal. However, if dataset is too small (e.g. number of rows is smaller than 20), then using too many components will limit the choice of cross-validation splits.
            </p>
            <p>
               You will have a chance to fine tune the number of components for PV-set generation later.
            </p>
            {/if}

            {#if model !== null && scalars_ini === null}
            <p><strong>Step 3</strong></p>
            <p>
               Now set necessary parameters for Procrustes cross-validation method and click button to compute the preliminary version of the PV-set. The parameters are the same as you use for conventional cross-validation: type of split and number of segments. It is recommended to use fewer number of segments (e.g. 3, 4 or 5). The more segments you use the more time it will take to compute the PV-set.
            </p>
            {/if}
            {#if model !== null && scalars_ini !== null && pvset === null}
            <p><strong>Step 4</strong></p>
               {#if pvoptions.ncompRec === model.ncomp}
               <p>
               The number of components you have selected seems to be fine for the given split settings &mdash; all scalars are within the expected limits of [0, 2]. You can reduce the number of components, if necessary, or keep the recommended version. Use scalars plot shown below as a guide for the selection.
               </p>

               {:else}
               <p>
                  The selected number of components is too high for creating a good quality PV-set. For some of the components $c_k/c$ scalars are outside the expected limits of [0, 2] and this can lead to a large discrepancy between the generated PV-set and the training set. It is recommended to adjust the number of components
               </p>
               <p>
                  The recommended value is currently selected. You can keep it as is, or change it to a lower value. You can also select a higher value but use such PV-set with caution. Use scalars plot shown below as a guide for the selection.
               </p>
               {/if}
            <p style="height:100%;">
            <ScalarsPlot scalars={scalars_ini} pvoptions={pvoptions} params={{plotType: 'hm'}}/>
            </p>
            {/if}
         {/if}
         </div>
      {:else}
         {#key pvres}
         <AppPlots {model} {res} {scalars} {pvoptions} />
         {/key}
      {/if}
   </div>
</div>

<style>

   .app-container {
      font-family: Helvetica, Arial, sans-serif;
      font-size: 1rem;
      color: #404040;
      box-sizing: border-box;
      margin: 0 auto;
      padding: 0.35em 0.25em;
      width: 100%;
      height: 100%;
      display: grid;
      grid-template-areas: "controls content";
      grid-template-rows: 100%;
      grid-template-columns: min(310px, 30%) 1fr;
   }

   .app-controls {
      box-sizing: border-box;
      padding: 0.25em 0.5em;
   }

   .app-content {
      box-sizing: border-box;
      padding: 0 1em 0 0.5em;
      padding: 0.25em 0.25em;
   }

   .app-controls > div {
      background: #e8e8e8;
      border-radius: 0.5rem;
   }

   .help-text {
      box-sizing: border-box;
      margin-top: 0.25rem;
      height: 100%;
      display: flex;
      flex-direction: column;
   }

   .help-text > p {
      background: #fafafa;
      padding: 0em 1.5em 0.5em 1.5em;
      color: #606060;
      line-height: 1.5em;
      margin: 0 1em 0 0;
   }

   .help-text > p:first-child {
      border-top-left-radius: 0.25rem;
      border-top-right-radius: 0.25rem;
      padding-top: 1.5em;
   }

   .help-text > p:last-child {
      border-bottom-left-radius: 0.25rem;
      border-bottom-right-radius: 0.25rem;
      padding-bottom: 1.5em;
   }

   @media(min-width:1300px) {
      .app-container {
         grid-template-columns: min(340px, 30%) 1fr;
      }
   }

   @media(min-width:1100px) {
      .app-container {
         grid-template-columns: min(320px, 30%) 1fr;
      }
   }

</style>